// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useRef, useEffect } from "react"
import { usePrevious } from "react-use"
import { equals } from "ramda"
import Highlighter from "react-highlight-words"
import { ListItemFavouriteType, ListItemType } from "./types"
import {
  List,
  ListItem,
  ListItemButton,
  StyledFavouriteIcon,
  StyledUnfavouriteIcon,
} from "./styles"
import { t } from "@lingui/macro"
import { ListItemDescription, ListItemLabel } from "../styles"
import { extractSearchWords } from "../../../lib/utils"

type Props = {
  header: React.ReactNode,
  list: ListItemType[],
  addedItemIds: string[],
  favouritesList?: ListItemFavouriteType[],
  activeId: string | undefined,
  onMouseMove: () => void,
  onAddItemToDestination: (id: string) => void,
  onFavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void,
  onUnfavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void,
  searchText?: string,
  showFavouriteButton: boolean,
}

const SearchDropdownList = ({
  header,
  list,
  addedItemIds,
  favouritesList = [],
  activeId,
  onMouseMove,
  onAddItemToDestination,
  onFavouriteItem,
  onUnfavouriteItem,
  searchText,
  showFavouriteButton,
}: Props): React.ReactElement<Props> => {
  const listRef = useRef<null | HTMLUListElement>(null)
  const prevList = usePrevious(list)
  const searchWords = searchText ? extractSearchWords(searchText).map(t => t.startsWith("+") ? t.substring(1) : t) : []
  useEffect(() => {
    if (!equals(list, prevList) && listRef.current !== null) {
      listRef.current.scrollTop = 0
    }
  }, [list, searchText])
  return <>
    {header}
    <List ref={listRef}>
      {list.map(({
        id,
        label,
        description,
      }: ListItemType) => {
        const favouriteItemRef = favouritesList.find(item => item.label === label)
        const isAdded = addedItemIds.some(itemId => itemId === id)
        return (
          <ListItem
            key={id}
            id={activeId === id ? id : ""}
            isAdded={isAdded}
            isActive={activeId === id}
            title={isAdded ? t({ id: "label_already_added", message: "Already added" }) : ""}
            onClick={(): void => {
              if (!isAdded) {
                onAddItemToDestination(id)
              }
            }}
            onMouseMove={onMouseMove}
          >
            {showFavouriteButton && (favouriteItemRef !== undefined ? (
              <ListItemButton
                type="button"
                title={t({ id: "label_unfavourite", message: "Unfavourite" })}
                onClick={(event): void => onUnfavouriteItem(event, favouriteItemRef.id)}
              >
                <StyledUnfavouriteIcon name="unfavourite"/>
              </ListItemButton>
            ) : (
              <ListItemButton
                type="button"
                title={t({ id: "label_favourite", message: "Favourite" })}
                onClick={(event): void => onFavouriteItem(event, id)}
              >
                <StyledFavouriteIcon name="favourite"/>
              </ListItemButton>
            )
            )}
            <ListItemLabel
              as={Highlighter}
              autoEscape
              searchWords={searchWords}
              textToHighlight={label}
            />
            {description && (
              <ListItemDescription
                as={Highlighter}
                autoEscape
                searchWords={searchWords}
                textToHighlight={description}
                title={description}
              />
            )}
          </ListItem>
        )
      })}
    </List>
  </>
}

export default SearchDropdownList
